import React, { useRef, useState } from 'react';
import api from '../api';
import { useAuth } from '../context/auth';
import { usePlayback } from '../context/playback';
import { trackEvent } from '../analytics';
import { useToast } from '../components/Toast';
import AppleMusicPlaylistDialog from '../components/AppleMusicPlaylistDialog';

// Making a list into a playlist in the reader's Apple Music library. Offered
// only to someone signed in here with Apple Music connected. Apple's API can
// add a playlist but never change or delete one, so the server remembers
// each one made: doing it again offers to open that playlist, or to make
// another.

export function useSaveListToAppleMusic(listId: string) {
  const auth = useAuth();
  const playback = usePlayback();
  const canSave = !!(auth.authTokens && auth.authTokens.token) && !!playback.isAppleMusicConnected;
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);
  const toast = useToast();
  const [dialog, setDialog] = useState<{
    kind: 'created' | 'existing';
    savedAt: string | null;
    missingCount: number;
    playlistURL: string | null;
  } | null>(null);

  function freshMusicUserToken(): string | undefined {
    try {
      const w = window as any;
      return (w.MusicKit && w.MusicKit.getInstance().musicUserToken) || undefined;
    } catch (e) {
      return undefined;
    }
  }

  function save(remake: boolean) {
    if (inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    // A long list takes a while: Apple is asked about every song.
    toast.busy('Creating playlist on Apple Music...');
    api
      .post('/api/music/lists/' + listId + '/playlist', {
        musicUserToken: freshMusicUserToken(),
        remake: remake,
      })
      .then((res: any) => {
        inFlight.current = false;
        setSaving(false);
        const d = res.data || {};
        if (d.alreadySaved) {
          toast.hide();
          setDialog({
            kind: 'existing',
            savedAt: d.savedAt || null,
            missingCount: 0,
            playlistURL: d.playlistURL || null,
          });
          return;
        }
        trackEvent('list_saved_to_apple_music', {
          list_id: listId,
          songs: d.songCount,
          missing: d.missingCount,
          remake: remake,
        });
        // Where to find it, in a popup: a new playlist can take a while to
        // show in the library, and no link opens it there directly.
        toast.hide();
        setDialog({
          kind: 'created',
          savedAt: d.savedAt || null,
          missingCount: d.missingCount || 0,
          playlistURL: d.playlistURL || null,
        });
      })
      .catch((err: any) => {
        inFlight.current = false;
        setSaving(false);
        // 401: Apple refused the member's token. 400: no connection on file
        // and none could be made. Either way, reconnecting is the fix. 422:
        // Apple Music has none of the list's music.
        const status = err && err.response && err.response.status;
        toast.show(
          status === 401 || status === 400
            ? 'Your Apple Music connection has expired. Reconnect it in Streaming Preferences.'
            : status === 422
              ? "None of this list's music is on Apple Music."
              : "Couldn't save to Apple Music. Try again.",
          4000
        );
      });
  }

  return {
    canSave: canSave,
    saving: saving,
    save: function () {
      save(false);
    },
    // The confirmation toast and the "already made" dialog, rendered by
    // whoever offers the button.
    ui: (
      <>
        {toast.element}
        {dialog && (
          <AppleMusicPlaylistDialog
            kind={dialog.kind}
            savedAt={dialog.savedAt}
            missingCount={dialog.missingCount}
            playlistURL={dialog.playlistURL}
            onClose={() => setDialog(null)}
            onMakeAnother={() => {
              setDialog(null);
              save(true);
            }}
          />
        )}
      </>
    ),
  };
}
